import { Manager } from '../Manager';
import { getCollectionInProgress, getCollectionProgress } from '../../Utils/CollectableUtils';
import Core from '../../Core/Core';
import { LevelActions, LevelEmitter } from './LevelEmitter';
import { Actions } from '../../State/Actions/Actions';
import { getLevelReward, IRewardItem } from '../../State/reducers/score';
import FTUETracking, { FTUE_TRACK_STAGES } from '../Tracking/FTUETracking';
import { HudActions, HudEmitter } from '../Hud/HudEmitter';
import LevelView from '../../View/LevelView';
import SoundManager from '../../Sound/SoundManager';
import { BoardManagerUtils } from '../Board/BoardManagerUtils';
import { SkippableAnimation } from '../../UI/Animations/SkippableAnimation';
import { Suits } from '../../Tiles/Suits';
import { getMatchScore } from '../../Config/Tiles';
import { TileSprite } from '../../UI/Game/Board/TileSprite';
import { LevelUtils } from './LevelUtils';
import Tile from '../../Tiles/Tile';
import { IBoardMap } from '../../Game/BoardArrangement';
import { waitForMs } from '../../Utils/AsyncUtils';
import { IGameState } from '../../State/Store/store';
import { Tracking } from '../Tracking/Tracking';

export default class WinSequence extends Manager {
    private view: LevelView;

    constructor(view: LevelView) {
        super();

        this.view = view;

        this.addSelector((state: IGameState, pre: IGameState) => {
            this.levelComplete(state, pre);
        });
    }

    public cleanUp() {
        super.cleanUp();
    }

    private levelComplete(state: IGameState, previous: IGameState) {
        if (state.StateLevel.complete && !previous.StateLevel.complete) {
            this.handleLevelComplete(state.StateScore.score, state.StatePlayer.replaying).then(
                () => {
                    Tracking.trackMissionComplete();
                }
            );
        }
    }

    private async handleLevelComplete(score: number, replay: boolean) {
        SoundManager.stopSound('game-music', true, 1000);
        const { StateBoard, StateScore, StateLevel: level } = Core.store.getState();
        const pairs = BoardManagerUtils.findBoardMatches(StateBoard);
        const animation = new SkippableAnimation();
        const maxDuration = 3000;
        const groupSize = Math.ceil(pairs.length / (maxDuration / TileSprite.TILE_MATCH_TIME));
        for (let i = 0; i < pairs.length; i += groupSize) {
            for (let j = 0; j < groupSize; j++) {
                if (i + j >= pairs.length) {
                    break;
                }
                const pair = pairs[i + j];
                const anim = () => {
                    const suit = pair[0].tileType.suit as Suits;
                    const overScore =
                        suit === Suits.BOOSTER ? 0 : getMatchScore(suit) * StateScore.multiplier;
                    score += overScore;
                    this.handleEndMatch(pair, StateBoard.boardMap, overScore);
                };
                animation.add(anim);
                animation.addDelay(TileSprite.TILE_MATCH_TIME * 0.1);
            }
            animation.addDelay(TileSprite.TILE_MATCH_TIME);
        }

        await animation.play();

        const starsWon = LevelUtils.getStarsWon(StateScore, score);
        Core.store.dispatch(
            Actions.updateMapData({
                id: level.levelId,
                stars: starsWon,
            })
        );

        const rewards = getLevelReward(starsWon, Core.store.getState().StatePlayer.replaying);

        await this.showWinSequence(rewards, score, replay);

        return Promise.resolve();
    }

    private handleEndMatch(pair: [Tile, Tile], boardMap: IBoardMap, overScore: number) {
        const ids = pair.map(p => p.boardId);
        const coords = this.view.board.showTileMatch(ids, boardMap);

        waitForMs(TileSprite.TILE_MATCH_TIME).then(() => {
            this.view.board.showTileBurst(
                overScore,
                null,
                coords,
                pair[0].tileType.suit === 'gold'
            );
            Core.store.dispatch(Actions.addScore(overScore));
            Core.store.dispatch(Actions.setMultiplier(1));
        });
    }

    private async showWinSequence(rewards: IRewardItem[], score: number, replay: boolean) {
        const viewRef = this.view;
        const a = () => {
            viewRef.removeAllListeners();
            this.dismissWin();
        };

        LevelEmitter.instance.once(LevelActions.WIN_COMPLETE, a);

        Core.store.dispatch(Actions.updateHearts(1));
        const { levelIndex, timed } = Core.store.getState().StateLevel;
        const { winTime } = Core.store.getState().StateScore;
        const leaderScore = timed ? winTime : score;
        await Core.sdk.leaderboards.setLevelLeaderBoardData(levelIndex.toString(), leaderScore);

        const collectionInProgress = getCollectionInProgress();
        const collectionProgress = getCollectionProgress(collectionInProgress);

        await this.view.showWin(rewards, score, replay, collectionInProgress, collectionProgress);

        FTUETracking.track(FTUE_TRACK_STAGES.WIN_SHOWN, Core.store.getState());
    }

    private dismissWin() {
        this.view.removeAllListeners();
        FTUETracking.track(FTUE_TRACK_STAGES.WIN_DISMISSED, Core.store.getState());
        HudEmitter.instance.emit(HudActions.QUIT_GAME);
    }
}
